const PRODUCT_EVIDENCE_KEYS = [
  "product_type",
  "url",
  "permalink",
  "price",
  "image",
  "images",
  "seller",
  "sku",
  "item_number",
  "stock_status",
  "availability",
  "parent_product_id",
];

const SUPPLIER_EVIDENCE_KEYS = [
  "supplier_category",
  "supplier_market",
  "service_type",
  "shop_address",
  "button_link",
  "rfq_address",
  "imageURL",
  "qcb",
  "featured_position",
  "button_text",
];

function hasOwnKey(hit, key) {
  return Object.prototype.hasOwnProperty.call(hit, key);
}

function hasEvidence(hit, keys) {
  return keys.some(key => hasOwnKey(hit, key));
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function firstNonEmptyString(values) {
  return values.find(isNonEmptyString) || "";
}

function normalizeDestination(value) {
  if (!isNonEmptyString(value)) return "";

  const destination = value.trim();
  if (destination.startsWith("#") || destination.startsWith("?")) return "";

  try {
    const parsed = new URL(destination, "https://charli.invalid/");
    if (!["http:", "https:"].includes(parsed.protocol)) return "";
  } catch {
    return "";
  }

  return destination;
}

function firstValidDestination(hit, keys) {
  for (const key of keys) {
    const destination = normalizeDestination(hit[key]);
    if (destination) return destination;
  }

  return "";
}

function normalizeProduct(hit) {
  const images = Array.isArray(hit.images) ? hit.images : [];

  return {
    name: isNonEmptyString(hit.name) ? hit.name : "Product",
    price: hit.price,
    image: firstNonEmptyString([hit.image, ...images]),
    link: firstValidDestination(hit, ["url", "permalink"]),
    seller: typeof hit.seller === "string" ? hit.seller : "",
    objectID: hit.objectID,
  };
}

const NAMED_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: " " };

// Supplier descriptions can hold HTML pasted from Word; cards need plain text.
export function toPlainText(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, " ")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<\/?[a-z][^>]*>/gi, " ")
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, code) => {
      if (code[0] !== "#") return NAMED_ENTITIES[code.toLowerCase()] ?? entity;
      const point = code[1].toLowerCase() === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isInteger(point) && point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
    })
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSupplier(hit) {
  return {
    title: firstNonEmptyString([hit.title, hit.name]) || "Supplier",
    description: toPlainText(hit.description),
    logo: firstNonEmptyString([hit.imageURL, hit.logo]),
    link: firstValidDestination(hit, ["shop_address", "button_link", "rfq_address"]),
    objectID: hit.objectID,
  };
}

export function processCharliHits(hits) {
  const results = { products: [], suppliers: [] };
  if (!Array.isArray(hits)) return results;

  hits.forEach(hit => {
    if (!hit || typeof hit !== "object" || Array.isArray(hit)) return;

    const isProduct = hasEvidence(hit, PRODUCT_EVIDENCE_KEYS);
    const isSupplier = hasEvidence(hit, SUPPLIER_EVIDENCE_KEYS);

    if (isProduct === isSupplier) return;

    if (isProduct) {
      results.products.push(normalizeProduct(hit));
      return;
    }

    results.suppliers.push(normalizeSupplier(hit));
  });

  return results;
}
